import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  PanelLeftClose,
  PanelLeftOpen,
  Star,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, initials } from "@/lib/constants";
import { canAccessModule } from "@/lib/permissions";
import { ADMIN_NAVIGATION } from "@/lib/adminNavigation";
import { api } from "@/lib/api";

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [experienceCount, setExperienceCount] = useState(0);
  const [practitionerCount, setPractitionerCount] = useState(0);
  const width = collapsed ? "w-14" : "w-60";
  const items = ADMIN_NAVIGATION.filter((i) => canAccessModule(user, i.module) && (!i.adminOnly || user?.role === "admin"));

  useEffect(() => {
    if (user?.role !== "admin") return;
    let active = true;
    const loadCount = () => Promise.all([
      api.get("/experiences/inbox", { params: { unread_only: true } }),
      api.get("/practitioner-approvals"),
    ])
      .then(([experiences, practitioners]) => {
        if (!active) return;
        setExperienceCount(experiences.data.unread_count || 0);
        setPractitionerCount(practitioners.data.pending_count || 0);
      })
      .catch(() => {});
    loadCount();
    const timer = setInterval(loadCount, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [user?.role, location.pathname]);

  return (
    <aside
      className={cn(
        "h-full min-h-0 border-r border-pleiades-border bg-pleiades-sidebar shrink-0 flex flex-col transition-[width] duration-150",
        width
      )}
      data-testid="sidebar"
    >
      <div className="h-14 px-3 flex items-center gap-2 border-b border-pleiades-border">
        <div className="w-8 h-8 flex items-center justify-center bg-pleiades-blue text-white rounded-sm shrink-0">
          <Star className="w-4 h-4" strokeWidth={1.5} />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="text-[13px] font-semibold leading-tight truncate">Pleiades</div>
            <div className="text-[11px] text-pleiades-slate leading-tight truncate">Rehberlik CRM</div>
          </div>
        )}
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-2">
        {items.map((item) => {
          const Icon = item.icon;
          const badgeCount = item.badge === "experiences"
            ? experienceCount
            : item.badge === "practitioners"
              ? practitionerCount
              : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                cn(
                  "relative group flex items-center gap-2.5 mx-1.5 my-0.5 px-2 py-1.5 rounded-sm text-[13px] transition-colors",
                  isActive
                    ? "bg-white text-pleiades-ink border border-pleiades-border"
                    : "text-pleiades-slate hover:bg-white/70 hover:text-pleiades-ink border border-transparent"
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <Icon className="w-[16px] h-[16px] shrink-0" strokeWidth={1.6} />
              {!collapsed && <span className="truncate flex-1">{item.label}</span>}
              {item.badge && badgeCount > 0 && (
                <span className={`${collapsed ? "absolute right-1 top-1" : ""} min-w-5 h-5 px-1 grid place-items-center rounded-full bg-pleiades-blue text-white text-[10px] font-semibold`}>
                  {badgeCount > 99 ? "99+" : badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-pleiades-border p-2">
        {!collapsed ? (
          <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-sm hover:bg-white/70 group">
            <div className="pleiades-initials-avatar w-7 h-7 rounded-sm text-[11px] flex items-center justify-center font-medium shrink-0">
              {initials(user?.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-medium leading-tight truncate">{user?.name}</div>
              <div className="text-[11px] text-pleiades-slate leading-tight truncate">
                {ROLE_LABEL[user?.role] || user?.role}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="btn-logout"
              className="text-[11px] text-pleiades-slate hover:text-pleiades-ink opacity-0 group-hover:opacity-100 transition-opacity"
            >
              Çıkış
            </button>
          </div>
        ) : (
          <button
            onClick={logout}
            data-testid="btn-logout-collapsed"
            className="pleiades-initials-avatar w-full h-8 flex items-center justify-center rounded-sm text-[11px]"
            title={user?.name}
          >
            {initials(user?.name)}
          </button>
        )}
        <button
          onClick={onToggle}
          data-testid="btn-sidebar-toggle"
          className="mt-1 w-full flex items-center justify-center gap-2 py-1 text-[11px] text-pleiades-slate hover:text-pleiades-ink"
        >
          {collapsed ? (
            <PanelLeftOpen className="w-3.5 h-3.5" />
          ) : (
            <>
              <PanelLeftClose className="w-3.5 h-3.5" /> Daralt
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
